import React, { useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Dialog, DialogTitle, DialogContent, DialogActions, CircularProgress } from '@material-ui/core';
import '../../assets/css/timeline.css';
import * as Utils from '../../commons/utils/Utils.js';
import LocaleFormat from '../../commons/utils/LocaleFormat.js';
import Security from '../../commons/security/Security.js';

const BASE_URL = '/inventario/tarifarios';

/**
 * VEC-3499 (def. 9) — historial del tarifario, el reloj de la sección "Ítems del Catálogo".
 *
 * Cada entrada: fecha y hora - evento - usuario (Manual | Importación), y debajo el detalle. En los
 * cambios de montos y costo fijo, cuando el tarifario no tenía valor propio, el backend manda el
 * valor del Catálogo de ese momento marcado con `anteriorEsCatalogo` / `nuevoEsCatalogo`.
 *
 * Misma presentación que InventarioManoObraHistorialModal (timeline.css, header teal, cards).
 *
 * Una importación muestra solo la cantidad de ítems; el detalle (cada ítem con sus valores antes y
 * después) se baja como Excel, y solo con TARIFARIOS_VISUALIZAR_HISTORIAL_DETALLE. El backend
 * también lo exige: el permiso no se limita a ocultar el botón.
 */
const InventarioTarifariosHistorialModal = (props) => {
  const intl = useIntl();

  const [loading, setLoading] = useState(false);
  const [filas, setFilas] = useState([]);
  const [descargandoId, setDescargandoId] = useState(null);

  const puedeVerDetalle = Security.hasPermission('TARIFARIOS_VISUALIZAR_HISTORIAL_DETALLE');

  useEffect(() => {
    if (!props.open) {
      return;
    }
    setLoading(true);
    Utils.getData(`${BASE_URL}/${props.tarifarioId}/historial`)
      .then((data) => setFilas(data || []))
      .catch(() => setFilas([]))
      .finally(() => setLoading(false));
  }, [props.open, props.tarifarioId]);

  const labelDeCampo = (campo) => {
    switch (campo) {
      case 'nombre':
        return intl.formatMessage({ id: 'inventarioTarifarios.historial.campo_nombre', defaultMessage: 'Nombre' });
      case 'descripcion':
        return intl.formatMessage({ id: 'inventarioTarifarios.historial.campo_descripcion', defaultMessage: 'Descripción' });
      case 'costo':
        return intl.formatMessage({ id: 'inventarioTarifarios.historial.campo_costo', defaultMessage: 'Costo' });
      case 'valor_maximo':
        return intl.formatMessage({ id: 'inventarioTarifarios.historial.campo_valor_maximo', defaultMessage: 'Valor máximo' });
      case 'costo_fijo':
        return intl.formatMessage({ id: 'inventarioTarifarios.historial.campo_costo_fijo', defaultMessage: 'Costo fijo' });
      default:
        return campo;
    }
  };

  const labelDeEvento = (fila) => {
    switch (fila.evento) {
      case 'alta':
        return intl.formatMessage({ id: 'inventarioTarifarios.historial.evento_alta', defaultMessage: 'Alta del tarifario' });
      case 'baja':
        return intl.formatMessage({ id: 'inventarioTarifarios.historial.evento_baja', defaultMessage: 'Baja del tarifario' });
      case 'proveedor_asociado':
        return intl.formatMessage({ id: 'inventarioTarifarios.historial.evento_proveedor_asociado', defaultMessage: 'Proveedor asociado' });
      case 'proveedor_desasociado':
        return intl.formatMessage({
          id: 'inventarioTarifarios.historial.evento_proveedor_desasociado',
          defaultMessage: 'Proveedor desasociado',
        });
      case 'importacion':
        return intl.formatMessage({ id: 'inventarioTarifarios.historial.evento_importacion', defaultMessage: 'Importación' });
      case 'modificacion':
      case 'cambio_item':
        return intl.formatMessage(
          { id: 'inventarioTarifarios.historial.evento_cambio', defaultMessage: 'Cambio de {campo}' },
          { campo: labelDeCampo(fila.campo) }
        );
      default:
        return fila.evento;
    }
  };

  const labelDeOrigen = (origen) =>
    origen === 'importacion'
      ? intl.formatMessage({ id: 'inventarioTarifarios.historial.origen_importacion', defaultMessage: 'Importación' })
      : intl.formatMessage({ id: 'inventarioTarifarios.historial.origen_manual', defaultMessage: 'Manual' });

  /** Montos con formato local, costo fijo como Si/No; sin valor (p. ej. "sin tope") va un guion. */
  const formatValor = (campo, valor) => {
    if (campo === 'costo_fijo') {
      return valor === '1'
        ? intl.formatMessage({ id: 'yes', defaultMessage: 'Si' })
        : intl.formatMessage({ id: 'no', defaultMessage: 'No' });
    }
    if (valor === null || valor === undefined || valor === '') {
      return '-';
    }
    if (campo === 'costo' || campo === 'valor_maximo') {
      const numero = parseFloat(valor);
      return isNaN(numero) ? String(valor) : LocaleFormat.formatNumber(numero, 2);
    }
    return String(valor);
  };

  const conMarcaCatalogo = (texto, esCatalogo) =>
    esCatalogo
      ? texto + ' (' + intl.formatMessage({ id: 'inventarioTarifarios.historial.catalogo', defaultMessage: 'Catálogo' }) + ')'
      : texto;

  const itemLabel = (item) => {
    if (!item) return '-';
    return item.numeroDeParte ? item.descripcion + ' (' + item.numeroDeParte + ')' : item.descripcion;
  };

  const cambio = (fila) =>
    labelDeCampo(fila.campo) +
    ': ' +
    conMarcaCatalogo(formatValor(fila.campo, fila.valorAnterior), fila.anteriorEsCatalogo) +
    ' → ' +
    conMarcaCatalogo(formatValor(fila.campo, fila.valorNuevo), fila.nuevoEsCatalogo);

  const detalleDe = (fila) => {
    switch (fila.evento) {
      case 'alta':
        return fila.valorNuevo;
      case 'proveedor_asociado':
      case 'proveedor_desasociado':
        return fila.proveedor;
      case 'importacion':
        // valorNuevo trae la cantidad de ítems que escribió el archivo.
        return intl.formatMessage(
          {
            id: 'inventarioTarifarios.historial.detalle_importacion',
            defaultMessage: '{cantidad} ítems importados',
          },
          { cantidad: parseInt(fila.valorNuevo, 10) || 0 }
        );
      case 'modificacion':
        return cambio(fila);
      case 'cambio_item':
        return itemLabel(fila.item) + ' - ' + cambio(fila);
      default:
        return null;
    }
  };

  const usuarioDe = (fila) =>
    fila.usuario
      ? fila.usuario
      : intl.formatMessage({ id: 'inventarioTarifarios.historial.usuario_sistema', defaultMessage: 'Generado por el sistema' });

  const descargarDetalle = (fila) => {
    setDescargandoId(fila.id);
    Utils.dataBlob(`${BASE_URL}/${props.tarifarioId}/historial/${fila.id}/detalle/exportar`, 'GET', {})
      .then((res) => {
        const url = window.URL.createObjectURL(new Blob([res.data]));
        const link = document.createElement('a');
        link.href = url;
        // dataBlob devuelve el filename tal como viene del header, con las comillas incluidas.
        link.setAttribute('download', String(res.filename).replace(/"/g, ''));
        document.body.appendChild(link);
        link.click();
        link.parentNode.removeChild(link);
        window.URL.revokeObjectURL(url);
      })
      // Utils.dataBlob ya avisa el error por Swal; el catch solo deja correr el finally.
      .catch(() => {})
      .finally(() => setDescargandoId(null));
  };

  const botonDetalle = (fila) => {
    if (fila.evento !== 'importacion' || !puedeVerDetalle) {
      return null;
    }
    const descargando = descargandoId === fila.id;
    return (
      <button
        type="button"
        className="btn btn-sm btn-outline-secondary mt-1"
        disabled={descargando}
        onClick={() => descargarDetalle(fila)}
      >
        <i className={descargando ? 'la la-spinner spinner' : 'la la-download'}></i>{' '}
        <FormattedMessage id="inventarioTarifarios.historial.descargar_detalle" defaultMessage="Descargar detalle" />
      </button>
    );
  };

  const renderCard = (titulo, detalle, key, accion) => (
    <div className="timeline-card card border-grey border-lighten-2 card-history" key={key}>
      <div className="card-header">
        <h4 className="card-title">{titulo}</h4>
        {detalle && (
          <p className="card-subtitle text-muted pt-1">
            <span className="font-small-3">{detalle}</span>
          </p>
        )}
        {accion}
      </div>
    </div>
  );

  // 1140px = `.modal-xl`, inline por la misma razón que en InventarioManoObraHistorialModal.
  return (
    <Dialog open={!!props.open} onClose={props.onClose} maxWidth={false} fullWidth PaperProps={{ style: { maxWidth: 1140 } }}>
      <DialogTitle disableTypography className="modal-header bg-fleet">
        <h4 className="modal-title text-white mb-0">
          <i className="la la-history align-middle icon-modal-title"></i>
          <FormattedMessage id="inventarioTarifarios.historial.titulo" defaultMessage=" Historial del tarifario: " />
          {props.tarifarioNombre || ''}
        </h4>
      </DialogTitle>

      <DialogContent dividers className="modal-grey">
        {loading && (
          <div className="text-center my-3">
            <CircularProgress />
          </div>
        )}

        {!loading && (
          <div className="content-wrapper">
            <div className="content-body">
              <section id="timeline" className="timeline-left timeline-wrapper">
                <ul className="timeline">
                  <li className="timeline-line"></li>

                  {filas.length === 0 && (
                    <li className="timeline-item">
                      {renderCard(
                        intl.formatMessage({
                          id: 'inventarioTarifarios.historial.sin_cambios',
                          defaultMessage: 'Este tarifario no tiene movimientos registrados.',
                        }),
                        null,
                        'sin_cambios'
                      )}
                    </li>
                  )}

                  {filas.map((fila) => {
                    const esCambioCosto = fila.evento === 'cambio_item' && fila.campo === 'costo';
                    const fecha = fila.fechaHora ? LocaleFormat.formatDateTime(fila.fechaHora) : '-';
                    const titulo =
                      fecha + ' - ' + labelDeEvento(fila) + ' - ' + usuarioDe(fila) + ' (' + labelDeOrigen(fila.origen) + ')';

                    return (
                      <li key={'historial_' + fila.id} className={esCambioCosto ? 'timeline-item status-change' : 'timeline-item'}>
                        <div className="timeline-badge">
                          <span className="bg-fleet bg-lighten-1">
                            <i className={esCambioCosto ? 'la la-usd' : 'la la-history'}></i>
                          </span>
                        </div>
                        {renderCard(titulo, detalleDe(fila), 'card_' + fila.id, botonDetalle(fila))}
                      </li>
                    );
                  })}
                </ul>
              </section>
            </div>
          </div>
        )}

        <div className="text-muted mt-2" style={{ fontSize: 12 }}>
          <FormattedMessage
            id="inventarioTarifarios.historial.ayuda"
            defaultMessage="Se registran el alta y la baja del tarifario, los cambios de nombre y descripción, los proveedores asociados y desasociados, y los cambios de montos y Costo fijo de los ítems, manuales o por importación (un registro por cada importación)."
          />
        </div>
      </DialogContent>

      <DialogActions className="modal-grey">
        <button type="button" className="btn btn-default btn-fleet" onClick={props.onClose}>
          <FormattedMessage id="Ok" defaultMessage="Ok" />
        </button>
      </DialogActions>
    </Dialog>
  );
};

export default InventarioTarifariosHistorialModal;
